import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, Button, Badge, Separator } from "@tellescope/ui";
import { formatDate, formatCurrency, formatCardBrand, formatStripeSubscriptionInterval } from "@tellescope/utils";
import {
  CreditCard,
  Calendar,
  CheckCircle,
  AlertCircle,
  Clock,
  XCircle,
  Pause,
  Play,
  RotateCcw,
  X,
} from "lucide-react";
import {
  getStripePlanProductName,
  getSubscriptionResumesAt,
  isSubscriptionPaused,
} from "../../../hooks/useStripeSubscriptions";
import type { StripeSubscription } from "../../../hooks/useStripeSubscriptions";
import type { StripeSubscriptionsBlock } from "../../../../shared/schema";
import { useTellescopeQueryClient } from "@tellescope/tanstack-query";
import { useActionAvailability } from "./useActionAvailability";
import { useSubscriptionPricing } from "./useSubscriptionPricing";
import { CancelConfirmDialog } from "./CancelConfirmDialog";
import { CancelIframeDialog } from "./CancelIframeDialog";
import { PauseConfirmDialog } from "./PauseConfirmDialog";
import { PauseIframeDialog } from "./PauseIframeDialog";
import { ResumeConfirmDialog } from "./ResumeConfirmDialog";
import { UndoCancelConfirmDialog } from "./UndoCancelConfirmDialog";
import { UpdatePaymentMethodDialog } from "./UpdatePaymentMethodDialog";
import { i18n } from "@tellescope/i18n";

interface SubscriptionDetailDialogProps {
  subscription: StripeSubscription;
  blockProps: StripeSubscriptionsBlock["props"];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function getStatusInfo(status: StripeSubscription["status"]): {
  icon: React.ElementType;
  colorClass: string;
  badgeVariant: "default" | "secondary" | "destructive" | "outline";
  label: string;
} {
  switch (status) {
    case "active":
      return {
        icon: CheckCircle,
        colorClass: "text-green-600",
        badgeVariant: "default",
        label: i18n.t("Active", { ns: "purchases" }),
      };
    case "trialing":
      return {
        icon: Clock,
        colorClass: "text-blue-600",
        badgeVariant: "secondary",
        label: i18n.t("Trial", { ns: "purchases" }),
      };
    case "past_due":
      return {
        icon: AlertCircle,
        colorClass: "text-yellow-600",
        badgeVariant: "outline",
        label: i18n.t("Past Due", { ns: "purchases" }),
      };
    case "unpaid":
      return {
        icon: AlertCircle,
        colorClass: "text-orange-600",
        badgeVariant: "outline",
        label: i18n.t("Unpaid", { ns: "purchases" }),
      };
    case "canceled":
      return {
        icon: XCircle,
        colorClass: "text-gray-500",
        badgeVariant: "secondary",
        label: i18n.t("Canceled", { ns: "purchases" }),
      };
    case "incomplete":
    case "incomplete_expired":
      return {
        icon: AlertCircle,
        colorClass: "text-red-600",
        badgeVariant: "destructive",
        label: i18n.t("Incomplete", { ns: "purchases" }),
      };
    case "paused":
      return {
        icon: Pause,
        colorClass: "text-gray-500",
        badgeVariant: "secondary",
        label: i18n.t("Paused", { ns: "purchases" }),
      };
    default:
      return { icon: Clock, colorClass: "text-muted-foreground", badgeVariant: "outline", label: status };
  }
}

export function SubscriptionDetailDialog({
  subscription,
  blockProps,
  open,
  onOpenChange,
}: SubscriptionDetailDialogProps) {
  const { t } = useTranslation("payments");
  const tellescopeQueryClient = useTellescopeQueryClient();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [undoCancelDialogOpen, setUndoCancelDialogOpen] = useState(false);
  const [updatePaymentDialogOpen, setUpdatePaymentDialogOpen] = useState(false);

  const availability = useActionAvailability(subscription, blockProps);
  const statusInfo = getStatusInfo(subscription.status);
  const StatusIcon = statusInfo.icon;

  const { effectivePlan, resolvedItems } = subscription;
  const isMultiItem = resolvedItems.length > 1;
  const productName = getStripePlanProductName(effectivePlan) ?? t("Subscription");
  const pricing = useSubscriptionPricing(subscription);
  const price =
    resolvedItems.length > 0 ? formatCurrency(pricing.listTotal, pricing.currency, { fromSmallestUnit: true }) : null;
  const discountedPrice =
    pricing.hasDiscount && pricing.discountedTotal !== null
      ? formatCurrency(pricing.discountedTotal, pricing.currency, { fromSmallestUnit: true })
      : null;
  const interval = effectivePlan
    ? formatStripeSubscriptionInterval(effectivePlan.interval, effectivePlan.interval_count)
    : resolvedItems[0]
    ? formatStripeSubscriptionInterval(resolvedItems[0].interval, resolvedItems[0].interval_count)
    : null;
  const nextBillingDate = formatDate(subscription.current_period_end * 1000);
  const cardInfo = subscription.default_payment_method?.card;
  const isPaused = isSubscriptionPaused(subscription);
  const resumesAt = getSubscriptionResumesAt(subscription);
  const pausedUntilDate = resumesAt !== null ? formatDate(resumesAt * 1000) : null;

  const refreshSubscriptions = () => {
    tellescopeQueryClient.invalidateQueries({
      model: "integrations",
      method: "proxy_read",
    });
  };

  const handleActionSuccess = (message: string) => {
    toast.success(message);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("Subscription Details")}</DialogTitle>
          </DialogHeader>

          {/* Subscription summary */}
          <div className="flex gap-4 py-2">
            <div className="flex-shrink-0">
              <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center">
                <StatusIcon className={`h-6 w-6 ${statusInfo.colorClass}`} />
              </div>
            </div>
            <div className="flex-1 min-w-0 overflow-hidden">
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="font-semibold truncate flex-1 w-0">
                  {isMultiItem ? t("Multi-Product Subscription") : productName}
                </h3>
                <Badge className="flex-shrink-0" variant={statusInfo.badgeVariant}>
                  {statusInfo.label}
                </Badge>
              </div>
              {isMultiItem && (
                <div className="mt-1 space-y-0.5">
                  {pricing.itemBreakdown.map((item) => {
                    const itemHasDiscount = item.discountedAmount !== null && item.discountedAmount < item.listAmount;
                    return (
                      <div
                        key={item.subscriptionItemId ?? item.productName}
                        className="flex items-center justify-between text-xs text-muted-foreground"
                      >
                        <span>{item.productName || t("Subscription")}</span>
                        <span>
                          {itemHasDiscount && (
                            <span className="line-through mr-1">
                              {formatCurrency(item.listAmount, item.currency, { fromSmallestUnit: true })}
                            </span>
                          )}
                          {formatCurrency(item.discountedAmount ?? item.listAmount, item.currency, {
                            fromSmallestUnit: true,
                          })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              {price !== null && (
                <p className="text-lg font-bold text-primary">
                  {pricing.hasDiscount && discountedPrice !== null ? (
                    <span className="line-through text-sm font-normal text-muted-foreground">{price}</span>
                  ) : isMultiItem ? (
                    t("Total: {{price}}", { price })
                  ) : (
                    price
                  )}
                  <span className="text-sm font-normal text-muted-foreground">{interval}</span>
                </p>
              )}

              {pricing.hasDiscount && discountedPrice !== null && (
                <p className="text-sm text-muted-foreground mt-0.5">
                  {t("Next payment: {{price}}", { price: discountedPrice })}
                </p>
              )}
              {pricing.hasDiscount && pricing.discounts.length > 0 && (
                <div className="mt-1 space-y-0.5">
                  {pricing.discounts.map((discount) => {
                    const name = discount.name ?? t("Discount applied");
                    return (
                      <p key={discount.id} className="text-xs text-muted-foreground">
                        {discount.percentOff !== null
                          ? t("{{name}} ({{percentOff}}% off)", { name, percentOff: discount.percentOff })
                          : discount.amountOff !== null
                          ? t("{{name}} ({{amountOff}} off)", {
                              name,
                              amountOff: formatCurrency(discount.amountOff, pricing.currency, {
                                fromSmallestUnit: true,
                              }),
                            })
                          : name}
                      </p>
                    );
                  })}
                </div>
              )}
              {pricing.hasDiscount && pricing.discountExpiration?.type === "expiring" && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("Price increases to {{price}} on {{date}}", {
                    price,
                    date: formatDate(pricing.discountExpiration.date * 1000),
                  })}
                </p>
              )}
              {pricing.hasDiscount && pricing.discountExpiration?.type === "expiring-soon" && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("This discount applies to your next invoice only")}
                </p>
              )}
              {pricing.trialEndsAt !== null && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("Trial until {{date}}, then {{price}}", {
                    date: formatDate(pricing.trialEndsAt * 1000),
                    price: discountedPrice ?? price ?? "",
                  })}
                </p>
              )}
              {pricing.hasSchedule && (
                <p className="text-xs text-muted-foreground mt-0.5">{t("Your plan has an upcoming change")}</p>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="space-y-2 text-sm">
            {subscription.status === "active" && !subscription.cancel_at_period_end && !isPaused && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>
                  {t("Next billing")}: {nextBillingDate}
                </span>
              </div>
            )}

            {subscription.cancel_at_period_end && (
              <div className="flex items-center gap-2 text-amber-600">
                <Clock className="h-4 w-4" />
                <span>{t("Cancels on {{date}}", { date: nextBillingDate })}</span>
              </div>
            )}

            {isPaused && (
              <div className="flex items-center gap-2 text-muted-foreground">
                {pausedUntilDate !== null ? (
                  <>
                    <Calendar className="h-4 w-4" />
                    <span>{t("Paused until {{date}}", { date: pausedUntilDate })}</span>
                  </>
                ) : (
                  <>
                    <Pause className="h-4 w-4" />
                    <span>{t("Subscription is paused")}</span>
                  </>
                )}
              </div>
            )}

            {cardInfo && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <CreditCard className="h-4 w-4" />
                <span>
                  {formatCardBrand(cardInfo.brand)} ****{cardInfo.last4} ({cardInfo.exp_month}/{cardInfo.exp_year})
                </span>
              </div>
            )}
          </div>

          {/* Actions section */}
          {availability.hasAnyAction && (
            <>
              <Separator className="my-2" />
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">{t("Actions")}</p>

                {availability.canUpdatePaymentMethod && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setUpdatePaymentDialogOpen(true)}
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    {t("Update Payment Method")}
                  </Button>
                )}

                {availability.canUndoCancel && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setUndoCancelDialogOpen(true)}
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    {t("Reactivate Subscription")}
                  </Button>
                )}

                {availability.canResume && (
                  <Button variant="default" className="w-full justify-start" onClick={() => setResumeDialogOpen(true)}>
                    <Play className="h-4 w-4 mr-2" />
                    {t("Resume Subscription")}
                  </Button>
                )}

                {availability.canPause && (
                  <Button variant="outline" className="w-full justify-start" onClick={() => setPauseDialogOpen(true)}>
                    <Pause className="h-4 w-4 mr-2" />
                    {t("Pause Subscription")}
                  </Button>
                )}

                {availability.canCancel && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => setCancelDialogOpen(true)}
                  >
                    <X className="h-4 w-4 mr-2" />
                    {t("Cancel Subscription")}
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation dialogs */}
      {blockProps.cancelIframe ? (
        <CancelIframeDialog
          subscription={subscription}
          config={blockProps.cancelIframe}
          open={cancelDialogOpen}
          onOpenChange={(open) => {
            setCancelDialogOpen(open);
            if (!open) refreshSubscriptions();
          }}
          onSuccess={() => handleActionSuccess(t("Subscription canceled."))}
          onNavigateToPause={
            blockProps.pauseIframe
              ? () => {
                  setCancelDialogOpen(false);
                  setPauseDialogOpen(true);
                }
              : undefined
          }
          onPauseSuccess={() => handleActionSuccess(t("Subscription paused."))}
          onUnpauseSuccess={() => handleActionSuccess(t("Welcome back! Your subscription is active."))}
        />
      ) : (
        <CancelConfirmDialog
          subscription={subscription}
          open={cancelDialogOpen}
          onOpenChange={setCancelDialogOpen}
          onSuccess={() => handleActionSuccess(t("Subscription canceled."))}
        />
      )}

      {blockProps.pauseIframe ? (
        <PauseIframeDialog
          subscription={subscription}
          config={blockProps.pauseIframe}
          open={pauseDialogOpen}
          onOpenChange={(open) => {
            setPauseDialogOpen(open);
            if (!open) refreshSubscriptions();
          }}
          onSuccess={() => handleActionSuccess(t("Subscription paused."))}
          onNavigateToCancel={
            blockProps.cancelIframe
              ? () => {
                  setPauseDialogOpen(false);
                  setCancelDialogOpen(true);
                }
              : undefined
          }
        />
      ) : (
        <PauseConfirmDialog
          subscription={subscription}
          open={pauseDialogOpen}
          onOpenChange={setPauseDialogOpen}
          onSuccess={() => handleActionSuccess(t("Subscription paused."))}
        />
      )}

      <ResumeConfirmDialog
        subscription={subscription}
        open={resumeDialogOpen}
        onOpenChange={setResumeDialogOpen}
        onSuccess={() => handleActionSuccess(t("Welcome back! Your subscription is active."))}
      />

      <UndoCancelConfirmDialog
        subscription={subscription}
        open={undoCancelDialogOpen}
        onOpenChange={setUndoCancelDialogOpen}
        onSuccess={() => handleActionSuccess(t("Great! Your subscription will continue."))}
      />

      <UpdatePaymentMethodDialog
        subscription={subscription}
        open={updatePaymentDialogOpen}
        onOpenChange={setUpdatePaymentDialogOpen}
        onSuccess={() => handleActionSuccess(t("Payment method updated successfully."))}
      />
    </>
  );
}
